import { CustomDateFilter } from 'components/CustomDateFilter';
import { CustomTable } from 'components/CustomTable';
import { ITableColumn, PENDING_TRANSFER_LABEL } from 'constants/tables';
import { tableDate } from 'helpers/date';
import { formatNumberAsCurrency } from 'helpers/utils';
import { DashboardScreen } from 'helpers/mixpanelEvents';
import { useSessionStorage } from 'hooks/useSessionStorage';
import { FC, useEffect, useMemo, useState } from 'react';
import { RootState } from 'store/create-store';
import {
  getDonationListColumnVisibility,
  getDonationListSearchString,
  getDonationListSortState,
} from 'selectors/viewPreferences';
import { IDonorTotal } from 'services/donors/types';
import {
  donationListColumnVisibility,
  donationListSearchString,
  donationListSortState,
} from 'store/viewPreferences/slice';
import { ColumnVisibilityMap, ISortState } from 'store/viewPreferences/types';

interface IData {
  id: number;
  date: string;
  name: string;
  original_amount: string;
  amount: string;
  processing_fee: string;
  disbursement_amount: string;
  page_url: string;
  ref: string;
  email: string;
  address: string;
  address_2: string;
  city: string;
  state: string;
  zip: string;
  employer: string;
  occupation: string;
  employer_address: string;
  employer_address_2: string;
  employer_city: string;
  employer_state: string;
  employer_zip: string;
  transaction_id: string;
  payment_method: string;
  page_name: string;
  is_recurring_donation: string;
}

interface Props {
  donors?: IDonorTotal[];
  columns: ITableColumn[];
  sessionStoragePrefix: string;
  screen: DashboardScreen;
  prefSelectors: {
    columnVisibility: (state: RootState) => ColumnVisibilityMap | null;
    searchString: (state: RootState) => string | null;
    sortState: (state: RootState) => ISortState | null;
  };
  prefActions: {
    setColumnVisibility: (value: ColumnVisibilityMap | null) => any;
    setSearchString: (value: string | null) => any;
    setSortState: (value: ISortState) => any;
  };
}

const DonorList: FC<Props> = ({
  donors,
  columns,
  sessionStoragePrefix,
  screen,
  prefSelectors = {
    columnVisibility: getDonationListColumnVisibility,
    searchString: getDonationListSearchString,
    sortState: getDonationListSortState,
  },
  prefActions = {
    setColumnVisibility: donationListColumnVisibility,
    setSearchString: donationListSearchString,
    setSortState: donationListSortState,
  },
}) => {
  const [columnVisibility, setColumnVisibility] = useSessionStorage(
    `${sessionStoragePrefix}ColumnVisibility`,
    null,
    prefSelectors.columnVisibility,
    value => prefActions.setColumnVisibility(value)
  );

  const [searchString, setSearchString] = useSessionStorage(
    `${sessionStoragePrefix}SearchString`,
    null,
    prefSelectors.searchString,
    value => prefActions.setSearchString(value)
  );

  const [sortState, setSortState] = useSessionStorage(
    `${sessionStoragePrefix}SortState`,
    null,
    prefSelectors.sortState,
    value => prefActions.setSortState(value ?? { sortKey: null, reverse: null })
  );
  const [dataState, setDataState] = useState<IData[]>([]);

  useEffect(() => {
    if (donors) {
      setDataState(
        donors?.map((prop, key) => {
          return {
            id: key,
            date: tableDate(prop.time),
            name: prop.name,
            original_amount: `${prop.original_donation_amount ?? prop.donation_amount}`,
            amount: `${prop.donation_amount}`,
            processing_fee: `${prop.processing_fee ?? PENDING_TRANSFER_LABEL}`,
            disbursement_amount: `${prop.disbursement_amount ?? PENDING_TRANSFER_LABEL}`,
            page_url: prop.page ?? '',
            ref: prop.ref_code ?? '',
            email: prop.user_email ?? '',
            address: prop.user_address ?? '',
            address_2: prop.user_address_2 ?? '',
            city: prop.user_city ?? '',
            state: prop.user_state ?? '',
            zip: prop.user_zip ?? '',
            employer: prop.user_employer ?? '',
            occupation: prop.user_occupation ?? '',
            employer_address: prop.user_employer_address ?? '',
            employer_address_2: prop.user_employer_address_2 ?? '',
            employer_city: prop.user_employer_city ?? '',
            employer_state: prop.user_employer_state ?? '',
            employer_zip: prop.user_employer_zip ?? '',
            transaction_id: prop.transaction_id,
            payment_method: prop.payment_method ?? '',
            page_name: prop.page_name ?? '',
            is_recurring_donation: prop.is_recurring_donation ? 'Yes' : 'No',
          };
        })
      );
    }
  }, [donors]);

  const tableColumns = useMemo(() => {
    return columns.map(column => ({
      ...column,
      visible: (columnVisibility || {})[column.accessor] ?? column.visible,
    }));
  }, [columnVisibility, columns]);

  const updateColumnVisibility = (columns: ITableColumn[]) => {
    setColumnVisibility(
      columns.reduce<Record<string, boolean>>((acc, column) => {
        acc[column.accessor] = column.visible;
        return acc;
      }, {})
    );
  };

  const updateDefaultColumns = (columns: ITableColumn[]) => {
    localStorage.setItem(
      `${sessionStoragePrefix}DefaultColumnVisibility`,
      JSON.stringify(
        columns.reduce<Record<string, boolean>>((acc, column) => {
          acc[column.accessor] = column.visible;
          return acc;
        }, {})
      )
    );
    updateColumnVisibility(columns);
  };

  return (
    <>
      <div className="row custom-header-container">
        <div className="col-lg-4 col-md-4 col-sm-12 grid-column content-start">
          <span className="oath-h3 inkBlue">
            {formatNumberAsCurrency(
              donors?.reduce((acc, item) => acc + item.donation_amount, 0) ?? 0
            )}{' '}
            Raised
          </span>
          <span className="oath-body inkBlue">{donors?.length || 0} Donations</span>
        </div>
        <div className="grid-column col-lg-8 col-md-8 col-sm-12">
          <CustomDateFilter screen={screen} />
        </div>
      </div>
      <div className="row recent-donations-table">
        <div className="col-lg-12">
          <CustomTable
            key="donor-list"
            title="Most Recent Donations"
            subtitle="Listed in order of donation date"
            data={dataState}
            columns={tableColumns}
            withPagination
            withDownloadCSV
            withColumnFilter
            onSaveColumnDefaults={updateDefaultColumns}
            updateColumns={updateColumnVisibility}
            onSortChanged={setSortState}
            onSearchChanged={setSearchString}
            searchString={searchString}
            sortState={sortState}
            screen={screen}
          />
        </div>
      </div>
    </>
  );
};

export default DonorList;
